import { useRef } from 'react'
import { ALGOLIA_INDEX_SUFFIX } from 'env'
import { InstantSearch, Configure } from 'react-instantsearch-dom'
import { useAuth } from 'hooks/UseAuth'
import { FileTreeLocationPicker } from './components/FileTreeLocationPicker/FileTreeLocationPicker'
import Styles from './LocationPicker.module.scss'
import { CustomHits as Hits } from './components/Hits/Hits'
import { CustomSearchInput as SearchInput } from './components/SearchInput/SearchInput'
import { useAlgoliaClient } from 'providers/AlgoliaClientProvider'
import { LocationPickerFolder, SelectedFolder } from './LocationPicker'
import { useLocationPicker } from './hooks/useLocationPicker'
import { getIndexFilters } from 'screens/blaze/Search/SearchModal/components/useSearch'
import { Search } from 'blaze-ui/icons'

export type ActionType = 'copy' | 'move' | 'create' | 'search'

export interface LocationPickerContentProps {
  actionType: ActionType
  initialFolder: LocationPickerFolder | null | undefined
  onSelectFolder: ({
    selectedFolder,
    newWorkspaceId,
  }: {
    selectedFolder: SelectedFolder | null
    newWorkspaceId: number
  }) => void
  initialWorkspaceId?: number
  isRootDisabled: boolean
  disabledFolderIds?: string[]
  isWorkspaceSelectorEnabled?: boolean
  fileTitle?: string
  workspaceId: number
  onConfirm?: () => void
}

export const LocationPickerContent = ({
  actionType,
  initialFolder,
  onSelectFolder,
  initialWorkspaceId,
  isRootDisabled = true,
  disabledFolderIds = [],
  fileTitle,
  workspaceId,
  onConfirm,
}: LocationPickerContentProps) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const { handleHitClick, onSelect, searchInputValue, setSearchInputValue, activeFolder, searchPlaceholder } =
    useLocationPicker({
      actionType,
      initialFolder,
      onSelectFolder,
      initialWorkspaceId,
      fileTitle,
    })
  const auth = useAuth()
  const client = useAlgoliaClient()

  if (!auth || !workspaceId) return null

  return (
    <>
      <InstantSearch searchClient={client} indexName={`Article_${ALGOLIA_INDEX_SUFFIX}`}>
        <Configure hitsPerPage={20} filters={`${getIndexFilters({ workspaceId, auth })} AND project_index = 1`} />
        <div className={Styles.searchInput}>
          <SearchInput
            value={searchInputValue}
            setValue={setSearchInputValue}
            placeholder={`${searchPlaceholder}...`}
            keepIconInMobile
            icon={Search}
          />
        </div>
        <div
          ref={bodyRef}
          className={Styles.body}
          style={
            bodyRef.current?.offsetParent?.clientHeight && actionType === 'move'
              ? { maxHeight: bodyRef.current?.offsetParent?.clientHeight - 49 }
              : {}
          }
        >
          {searchInputValue.length === 0 ? (
            <FileTreeLocationPicker
              workspaceId={workspaceId}
              onSelect={(folder) => {
                onSelect(folder)
                onConfirm?.()
              }}
              activeFolder={activeFolder}
              isRootDisabled={isRootDisabled}
              disabledFolderIds={disabledFolderIds}
              inModal
            />
          ) : (
            <Hits
              activeFolderId={activeFolder?.id}
              onClick={(hit: unknown) => {
                handleHitClick(hit as any)
                onConfirm?.()
              }}
              disabledFolderIds={disabledFolderIds}
            />
          )}
        </div>
      </InstantSearch>
    </>
  )
}
